import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { documentsAPI, categoriesAPI } from '../../services/api'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

const emptyProduct = () => ({
  _key: Math.random().toString(36).slice(2),
  category: '',
  total_price: '',
})

/**
 * Parsuje dane z ocrResult.structured_data (format zwracany przez LLM engine)
 * i mapuje na pola formularza przeglądu paragonu.
 */
function parseOcrData(structuredData) {
  if (!structuredData) return {}
  const ri = structuredData.receipt_info || {}
  return {
    store_name: ri.store_name || '',
    store_nip: ri.store_nip || '',
    receipt_number: ri.receipt_number || '',
    transaction_date: ri.transaction_date || '',
    transaction_time: ri.transaction_time || '',
    total_amount: ri.total_amount != null ? String(ri.total_amount) : '',
    products: (structuredData.products || []).map(p => ({
      _key: Math.random().toString(36).slice(2),
      category: p.category || p.name || '',
      total_price: p.total_price != null ? String(p.total_price) : '',
    })),
  }
}

/**
 * Inicjalizuje dane formularza z istniejącego Receipt lub z danych OCR.
 */
function buildInitialForm(document) {
  const receipt = document?.receipt
  if (receipt) {
    return {
      store_name: receipt.store_name || '',
      store_nip: receipt.store_nip || '',
      receipt_number: receipt.receipt_number || '',
      transaction_date: receipt.transaction_date
        ? receipt.transaction_date.slice(0, 10)
        : '',
      transaction_time: receipt.transaction_time || '',
      total_amount: receipt.total_amount != null ? String(receipt.total_amount) : '',
      products: (receipt.products || []).map(p => ({
        _key: Math.random().toString(36).slice(2),
        category: p.category || p.name || '',
        total_price: p.total_price != null ? String(p.total_price) : '',
      })),
    }
  }
  return parseOcrData(document?.ocrResult?.structured_data)
}

const ReceiptReviewPage = () => {
  const { t } = useTranslation('documents')
  const { id } = useParams()
  const navigate = useNavigate()

  const { data: documentData, isLoading, error } = useQuery(
    ['document', id],
    () => documentsAPI.getDocument(id),
    { enabled: !!id }
  )

  const { data: categoriesData } = useQuery(
    'categories',
    () => categoriesAPI.getCategories({ active: true }),
    { staleTime: 5 * 60 * 1000 }
  )

  const document = documentData?.data?.document
  const categories = categoriesData?.data?.categories || []

  const [form, setForm] = useState(null)
  const [categoryId, setCategoryId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [duplicateInfo, setDuplicateInfo] = useState(null) // { existing_document_id, existing_document_title }

  // Inicjalizacja formularza po załadowaniu dokumentu
  useEffect(() => {
    if (document && form === null) {
      const initial = buildInitialForm(document)
      setForm(initial)

      // Pre-select kategorię z paragonu lub z ocrResult
      const existingCatId = document.receipt?.expense?.category_id
        || document.category_id
        || ''
      setCategoryId(existingCatId)
    }
  }, [document])

  // Auto-select kategoria na podstawie kategorii z OCR
  const OCR_CATEGORY_MAP = {
    sklep: 'sklep',
    restauracja: 'restaurac',
    transport: 'transport',
    gaz: 'gaz',
    woda: 'woda',
    prąd: 'prąd',
    zdrowie: 'zdrowie',
    rozrywka: 'rozrywka',
  }
  useEffect(() => {
    if (!document || !categories.length) return
    const ocrCategory = document.ocrResult?.structured_data?.category
    if (!ocrCategory || ocrCategory === 'inne') return
    const term = OCR_CATEGORY_MAP[ocrCategory.toLowerCase()]
    if (!term) return
    const match = categories.find(c => c.name.toLowerCase().includes(term))
    if (match) setCategoryId(prev => prev || match.id)
  }, [document, categories])

  const setField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const setProductField = (key, field, value) => {
    setForm(prev => ({
      ...prev,
      products: prev.products.map(p =>
        p._key === key ? { ...p, [field]: value } : p
      ),
    }))
  }

  // Obliczanie sumy z produktów
  const computedTotal = form?.products?.reduce((sum, p) => {
    const val = parseFloat(p.total_price)
    return sum + (isNaN(val) ? 0 : val)
  }, 0) ?? 0

  const buildPayload = () => ({
    store_name: form.store_name || null,
    store_nip: form.store_nip || null,
    receipt_number: form.receipt_number || null,
    transaction_date: form.transaction_date || null,
    transaction_time: form.transaction_time || null,
    total_amount: parseFloat(form.total_amount) || computedTotal,
    category_id: categoryId || null,
    products: (form.products || [])
      .filter(p => p.category.trim())
      .map(p => ({
        category: p.category.trim(),
        total_price: parseFloat(p.total_price) || 0,
      })),
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setDuplicateInfo(null)
    setSubmitting(true)
    try {
      await documentsAPI.confirmReceipt(id, buildPayload())
      toast.success(t('receiptReview.confirmSuccess'))
      navigate(`/documents/${id}`)
    } catch (err) {
      const data = err?.response?.data
      if (data?.duplicate) {
        setDuplicateInfo({
          existing_document_id: data.existing_document_id,
          existing_document_title: data.existing_document_title,
        })
      } else {
        toast.error(t('receiptReview.confirmError'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleForceConfirm = async () => {
    setDuplicateInfo(null)
    setSubmitting(true)
    try {
      await documentsAPI.confirmReceiptForce(id, buildPayload())
      toast.success(t('receiptReview.confirmSuccess'))
      navigate(`/documents/${id}`)
    } catch {
      toast.error(t('receiptReview.confirmError'))
    } finally {
      setSubmitting(false)
    }
  }

  if (isLoading || form === null) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text={t('loadingDetail')} />
      </div>
    )
  }

  if (error || !document) {
    return (
      <div className="text-center py-12">
        <p className="text-error-600">{t('errorDetail')}</p>
        <Link to="/dashboard/documents" className="inline-flex items-center mt-4 text-sm text-[#167e75] hover:underline">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          {t('backToList')}
        </Link>
      </div>
    )
  }

  const isVerified = document.receipt?.manually_verified === true && form !== null

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Link
            to={`/documents/${id}`}
            className="inline-flex items-center justify-center p-2 text-[var(--color-text)] hover:bg-[var(--color-hover)] rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-lg font-semibold text-[var(--color-text)]">
              {t('receiptReview.title')}
            </h1>
            <p className="text-sm text-[var(--color-text-muted)]">{document.title}</p>
          </div>
        </div>
        {isVerified && (
          <div className="flex items-center space-x-1.5 text-sm text-green-600">
            <CheckCircleIcon className="h-4 w-4" />
            <span>{t('receiptReview.alreadyVerified')}</span>
          </div>
        )}
      </div>

      {/* Ostrzeżenie o duplikacie */}
      {duplicateInfo && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-800">
            Ten paragon już istnieje w systemie
            {duplicateInfo.existing_document_title && `: "${duplicateInfo.existing_document_title}"`}.
          </p>
          <p className="text-sm text-amber-700 mt-1">Czy chcesz mimo to zapisać ten paragon?</p>
          <div className="flex gap-3 mt-3">
            <button
              type="button"
              onClick={handleForceConfirm}
              disabled={submitting}
              className="px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50"
            >
              Tak, zapisz mimo to
            </button>
            <Link
              to={`/documents/${duplicateInfo.existing_document_id}/review`}
              className="px-3 py-1.5 text-sm font-medium border border-amber-400 text-amber-700 rounded-md hover:bg-amber-100"
            >
              Przejdź do istniejącego
            </Link>
            <button
              type="button"
              onClick={() => setDuplicateInfo(null)}
              className="px-3 py-1.5 text-sm font-medium text-amber-600 hover:underline"
            >
              Anuluj
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Dane paragonu */}
        <Card>
          <Card.Header>
            <h3 className="text-sm font-semibold text-[var(--color-text)]">
              {t('receiptReview.title')}
            </h3>
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
              {t('receiptReview.subtitle')}
            </p>
          </Card.Header>
          <Card.Body>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                  {t('receiptReview.storeName')}
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={form.store_name}
                  onChange={e => setField('store_name', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                  {t('receiptReview.storeNip')}
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={form.store_nip}
                  onChange={e => setField('store_nip', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                  {t('receiptReview.receiptNumber')}
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={form.receipt_number}
                  onChange={e => setField('receipt_number', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                  {t('receiptReview.transactionDate')}
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={form.transaction_date}
                  onChange={e => setField('transaction_date', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                  {t('receiptReview.transactionTime')}
                </label>
                <input
                  type="time"
                  className="form-input"
                  value={form.transaction_time}
                  onChange={e => setField('transaction_time', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                  {t('receiptReview.categoryLabel')}
                </label>
                <select
                  className="form-select"
                  value={categoryId}
                  onChange={e => setCategoryId(e.target.value)}
                >
                  <option value="">{t('receiptReview.noCategory')}</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Produkty */}
        <Card>
          <Card.Header>
            <h3 className="text-sm font-semibold text-[var(--color-text)]">
              {t('receiptReview.products')}
            </h3>
          </Card.Header>
          <Card.Body className="p-0 overflow-x-auto">
            {form.products && form.products.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border-strong)] bg-[var(--color-table-header)]">
                    <th className="text-left px-3 py-2 text-xs font-medium text-[var(--color-text-muted)] w-full">
                      Kategoria
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                      Suma
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {form.products.map(product => (
                    <tr
                      key={product._key}
                      className="border-b border-[var(--color-border-strong)] last:border-0"
                    >
                      <td className="px-3 py-1.5">
                        <input
                          type="text"
                          className="form-input text-xs py-1"
                          value={product.category}
                          onChange={e => setProductField(product._key, 'category', e.target.value)}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-input text-xs py-1 text-right w-28"
                          value={product.total_price}
                          onChange={e => setProductField(product._key, 'total_price', e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-[var(--color-text-faint)] text-center py-6">
                Brak kategorii produktów
              </p>
            )}
          </Card.Body>
        </Card>

        {/* Suma i submit */}
        <Card>
          <Card.Body>
            <div className="flex items-end justify-between gap-4">
              <div className="flex-1 max-w-xs">
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                  {t('receiptReview.totalAmount')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-input font-semibold text-lg"
                  value={form.total_amount}
                  onChange={e => setField('total_amount', e.target.value)}
                  placeholder={computedTotal > 0 ? computedTotal.toFixed(2) : '0.00'}
                />
                {computedTotal > 0 && (
                  <p className="text-xs text-[var(--color-text-faint)] mt-1">
                    Suma produktów: {computedTotal.toFixed(2)} PLN
                  </p>
                )}
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={submitting}
              >
                {submitting ? t('receiptReview.confirming') : t('receiptReview.confirm')}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </form>
    </div>
  )
}

export default ReceiptReviewPage
